import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  useLazyGetBoostPlansQuery,
  useDeleteBoostPlansMutation,
  useUpdateBoostPlansMutation,
} from "../../service/boostPlans";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import { useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import React from "react";
import SwitchToggle from "../../components/SwitchToggle";
import Pagination from "../../components/Panigation";
import ConfirmationDialog from "../../Modals/confirmationModal";
import { showError, showToast } from "../../constants/toasts";
import usePermission from "../../hooks/usePermission";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const ManageBoostPlans = () => {
  const navigate = useNavigate();
  const { isAdd, isEdit, isDelete } = usePermission({
    label: "Manage Boost Plans",
    page_permission: "isView",
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [rows, setRows] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [selected, setSelected] = useState<any | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [getBoostPlans, { isLoading }] = useLazyGetBoostPlansQuery();
  const [updateBoostPlan, { isLoading: isUpdating }] =
    useUpdateBoostPlansMutation();
  const [deleteBoostPlan, { isLoading: isDeleting }] =
    useDeleteBoostPlansMutation();

  // Fetch list
  const load = async (p?: number) => {
    try {
      const res = await getBoostPlans({
        page: p || page,
        limit,
        ...(debouncedSearch && { search: debouncedSearch }),
      }).unwrap();
      if (res?.code === 200) {
        setRows(res?.data?.list || []);
        setTotalCount(res?.data?.totalCount || 0);
      }
    } catch (e: any) {
      showError(e?.data?.message || "Failed to fetch boost plans");
      setRows([]);
      setTotalCount(0);
    }
  };

  // Status toggle
  const handleStatusChange = async (item: any) => {
    try {
      const res = await updateBoostPlan({
        id: item?._id,
        body: { isBlocked: !item?.isBlocked },
      }).unwrap();
      if (res?.code === 200) {
        showToast(
          `Boost plan ${
            !item?.isBlocked ? "blocked" : "activated"
          } successfully`
        );
        setRows((prev) =>
          prev.map((r) =>
            r?._id === item?._id ? { ...r, isBlocked: !item?.isBlocked } : r
          )
        );
      }
    } catch (e: any) {
      showError(e?.data?.message || "Failed to update status");
    }
  };

  // Delete flow
  const openDelete = (item: any) => {
    setSelected(item);
    setDeleteOpen(true);
  };
  const closeDelete = () => {
    setDeleteOpen(false);
    setSelected(null);
  };
  const confirmDelete = async () => {
    if (!selected) return;
    try {
      const res = await deleteBoostPlan({ id: selected?._id }).unwrap();
      if (res?.code === 200) {
        showToast("Boost plan deleted successfully");
        closeDelete();
        if (rows.length === 1 && page > 1) {
          setPage((prev) => prev - 1);
          load(page - 1);
        } else {
          load(page);
        }
      }
    } catch (e: any) {
      showError(e?.data?.message || "Failed to delete boost plan");
    }
  };
  // Load on search/page change
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const totalPages = Math.ceil(totalCount / limit);

  return (
    <>
      <div className="main_title">
        <h1>Manage Boost Plans</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          Manage Boost Plans
        </p>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              setDebouncedSearchTerm={setDebouncedSearch}
              placeholder="Search..."
            />
          </div>
          {isAdd && (
            <div className="right_s">
              <Button
                className="btn btn_primary"
                onClick={() => navigate("/manage-boost-plans/add")}
              >
                <AddIcon /> Add Boost Plan
              </Button>
            </div>
          )}
        </div>
        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Duration(Days)</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Visibility</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows?.length > 0 ? (
                rows.map((item: any) => (
                  <TableRow hover key={item?._id}>
                    <TableCell>{item?.title || "-"}</TableCell>
                    <TableCell>{item?.price ?? "-"} GH₵</TableCell>
                    <TableCell>{item?.duration ?? "-"}</TableCell>
                    <TableCell>
                      {item?.category?.name || item?.category || "-"}
                    </TableCell>
                    <TableCell>{item?.createdAt || "-"}</TableCell>
                    <TableCell>
                      <SwitchToggle
                        disabled={!isEdit}
                        onChange={() => handleStatusChange(item)}
                        checked={!item?.isBlocked}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="table_actions v2">
                        <IconButton
                          onClick={() =>
                            navigate(`/manage-boost-plans/detail/${item?._id}`)
                          }
                        >
                          <img src="/static/images/eye_icon.svg" alt="View" />
                        </IconButton>
                        {isEdit && (
                          <IconButton
                            onClick={() =>
                              navigate(`/manage-boost-plans/edit/${item?._id}`)
                            }
                          >
                            <img
                              src="/static/images/edit2_icon.svg"
                              alt="Edit"
                            />
                          </IconButton>
                        )}
                        {isDelete && (
                          <IconButton onClick={() => openDelete(item)}>
                            <img
                              src="/static/images/trash2_icon.svg"
                              alt="Delete"
                            />
                          </IconButton>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow hover>
                  <TableCell colSpan={7}>
                    <Box textAlign={"center"}>No data found</Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {totalCount > 0 && (
          <Pagination
            module={rows}
            page={page}
            onPageChange={(newPage: number) => {
              setPage(newPage);
              load(newPage);
            }}
            totalPages={Math.ceil(totalCount / limit)}
            setPage={setPage}
            totalCount={totalCount}
          />
        )}

        <ConfirmationDialog
          open={deleteOpen}
          onClose={closeDelete}
          onConfirm={confirmDelete}
          title="Delete Boost Plan"
          confirmText="Delete"
          cancelText="Cancel"
          message={`Are you sure you want to delete boost plan "${
            selected?.title || ""
          }"?`}
          isLoading={isDeleting}
        />
      </div>
    </>
  );
};

export default ManageBoostPlans;
